import {
    DestroyRef,
    computed,
    effect,
    inject,
    Injectable,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    Router,
} from '@angular/router';
import {
    BUILDING_PERMISSION_PROBE,
    createHttpContextFromOptions,
} from '@quipex/core/primitives';
import {
    createQuery,
    readQuery,
    type QuerySettledStatus,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import {
    UsersApiService,
    type CurrentUserBuildingPermissionResponse,
} from '@quipex/shared/contracts';
import {
    defer,
    finalize,
    firstValueFrom,
    map,
    of,
    ReplaySubject,
    shareReplay,
    Subject,
    switchMap,
    takeUntil,
    timeout,
    catchError,
    tap,
    type Observable,
} from 'rxjs';
import { AuthService } from '../auth.service';
import { FETCH_TIMEOUT } from '../guards/models';
import { UserStore } from '../user/user.store';
import {
    BuildingAccessRecovery,
    type BuildingDenialDisposition,
} from './building-access-recovery';
import { findRouteParamInSnapshot } from './models';

const PERMISSION_FRESHNESS_MS = 30 * 60 * 1000;
export type BuildingAccessDecision =
    | 'granted'
    | 'lost'
    | 'unavailable'
    | 'unauthenticated'
    | 'superseded';
export interface BuildingAccessLoss {
    buildingId: string;
    buildingName: string | null;
    unsavedChanges: boolean;
}
type ProbeResult =
    | {
          kind: 'granted';
          permission: CurrentUserBuildingPermissionResponse;
          checkedAt: number;
      }
    | { kind: 'lost' }
    | {
          kind: 'unavailable' | 'unauthenticated';
          problem: ResolvedProblemDetails;
      }
    | { kind: 'superseded' };
interface NavigationPermission {
    navigationId: number;
    denialVersion: number;
    buildingId: string;
    result: ProbeResult;
}

@Injectable()
export class UserBuildingStore {
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly usersApi = inject(UsersApiService);
    private readonly auth = inject(AuthService);
    private readonly user = inject(UserStore);
    private readonly recovery = inject(BuildingAccessRecovery);
    private readonly activeBuildingId = signal('');
    private readonly query = createQuery<
        CurrentUserBuildingPermissionResponse,
        ResolvedProblemDetails
    >();
    public readonly buildingPermissionQuery = readQuery(this.query);
    public readonly buildingPermission = this.query.data;
    public readonly buildingPermission$ = toObservable(this.buildingPermission);
    public readonly buildingPermissionStatus$ = toObservable(
        this.query.liveStatus
    );
    public readonly buildingPermissionIsFetching$ = toObservable(
        this.query.isFetching
    );
    private readonly decision = signal<BuildingAccessDecision | 'checking'>(
        'checking'
    );
    public readonly accessDecision = this.decision.asReadonly();
    public readonly accessLost = computed(() => this.decision() === 'lost');
    private readonly loss = new Subject<BuildingAccessLoss>();
    private lossNotice: BuildingAccessLoss | null = null;
    public readonly accessLoss$ = this.loss.asObservable();
    private ended = new ReplaySubject<void>(1);
    public get accessEnded$(): Observable<void> {
        return this.ended.asObservable();
    }
    private readonly destroyed$ = new Subject<void>();
    private readonly identity = computed(
        () =>
            `${this.auth.activeAccount()?.sub ?? ''}:${this.user.me()?.id ?? ''}:${this.user.org()?.id ?? ''}`
    );
    private ownerIdentity = this.identity();
    private ownerAccount = this.auth.activeAccount()?.sub;
    private checkedAt = 0;
    private generation = 0;
    private disposed = false;
    private loggingOut = false;
    private unbind: (() => void) | null = null;
    private staged: NavigationPermission | null = null;
    private readonly probes = new Map<string, Observable<ProbeResult>>();
    private deniedVersion = 0;
    private denialCheck: Observable<BuildingDenialDisposition> | null = null;

    constructor() {
        effect(() => {
            if (this.identity() !== this.ownerIdentity) {
                const account = this.auth.activeAccount();
                if (account && account.sub !== this.ownerAccount)
                    this.user.ensureFreshForAuthenticatedAccount(account);
                this.resetOwner(this.readCurrentBuildingId());
                if (!this.router.currentNavigation?.() && this.principalReady())
                    this.reload();
            }
        });
        this.auth.logoutStarted$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.loggingOut = true;
                this.resetOwner('');
            });
        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    const id = this.readCurrentBuildingId();
                    const pending = this.staged;
                    const denialIsCurrent =
                        id !== this.activeBuildingId() ||
                        pending?.denialVersion === this.deniedVersion;
                    this.staged = null;
                    if (id !== this.activeBuildingId()) this.resetOwner(id);
                    if (
                        id &&
                        pending?.buildingId === id &&
                        denialIsCurrent &&
                        pending.result.kind !== 'superseded'
                    ) {
                        this.apply(pending.result);
                    } else if (
                        id &&
                        this.query.isIdle() &&
                        this.principalReady()
                    ) {
                        this.reload();
                    }
                } else if (
                    event instanceof NavigationCancel ||
                    event instanceof NavigationError
                ) {
                    if (this.staged?.navigationId === event.id)
                        this.staged = null;
                    const committed = this.buildingPermission();
                    if (
                        committed &&
                        !this.denialCheck &&
                        this.decision() === 'checking'
                    ) {
                        this.query.setData(committed);
                        this.decision.set('granted');
                    } else if (this.query.isIdle() && this.principalReady()) {
                        this.reload();
                    }
                }
            });
        this.destroyRef.onDestroy(() => {
            this.disposed = true;
            this.endOwner();
            this.destroyed$.next();
            this.destroyed$.complete();
            this.loss.complete();
        });
        const id = this.readCurrentBuildingId();
        if (id) {
            this.resetOwner(id);
            this.verifyForNavigation$(id).subscribe();
        }
    }

    public currentBuildingId(): string {
        return this.activeBuildingId();
    }

    /** Target guards use the pending transaction; rendered features keep committed permissions. */
    public permissionForNavigation(): CurrentUserBuildingPermissionResponse | null {
        const pending = this.staged;
        if (
            pending &&
            pending.navigationId === this.navigationId() &&
            pending.denialVersion === this.deniedVersion
        ) {
            return pending.result.kind === 'granted'
                ? pending.result.permission
                : null;
        }
        return this.buildingPermission();
    }

    public verifyForNavigation$(
        buildingId: string
    ): Observable<BuildingAccessDecision> {
        return defer(() => {
            if (!buildingId || !this.isAlive())
                return of('superseded' as const);
            if (
                !this.router.currentNavigation?.() &&
                buildingId !== this.activeBuildingId()
            )
                this.resetOwner(buildingId);
            const navigationId = this.navigationId();
            if (
                navigationId &&
                this.staged?.navigationId === navigationId &&
                this.staged.buildingId === buildingId &&
                this.staged.denialVersion === this.deniedVersion
            ) {
                return of(this.staged.result.kind);
            }
            const cached = this.buildingPermission();
            if (
                buildingId === this.activeBuildingId() &&
                this.decision() === 'granted' &&
                cached &&
                Date.now() - this.checkedAt < PERMISSION_FRESHNESS_MS
            ) {
                return of('granted' as const);
            }
            const generation = this.generation;
            const denialVersion = this.deniedVersion;
            const checksCommittedBuilding =
                buildingId === this.activeBuildingId();
            if (buildingId === this.activeBuildingId()) {
                this.query.setLoading();
                this.decision.set('checking');
            }
            return this.probe$(buildingId).pipe(
                switchMap((result): Observable<ProbeResult> => {
                    if (
                        !checksCommittedBuilding ||
                        denialVersion === this.deniedVersion
                    )
                        return of(result);
                    const settled: Observable<unknown> =
                        this.denialCheck ?? of(null);
                    return settled.pipe(
                        map((): ProbeResult => {
                            const permission = this.buildingPermission();
                            if (this.accessLost()) return { kind: 'lost' };
                            if (
                                this.decision() === 'granted' &&
                                permission?.buildingId === buildingId
                            )
                                return {
                                    kind: 'granted',
                                    permission,
                                    checkedAt: this.checkedAt,
                                };
                            if (
                                this.decision() === 'unauthenticated' ||
                                this.decision() === 'unavailable'
                            )
                                return {
                                    kind:
                                        this.decision() === 'unauthenticated'
                                            ? 'unauthenticated'
                                            : 'unavailable',
                                    problem: this.query.error() ?? {
                                        status: 0,
                                        title: 'Unable to verify building access',
                                    },
                                };
                            return { kind: 'superseded' };
                        })
                    );
                }),
                map((result) => {
                    if (!this.isAlive() || generation !== this.generation)
                        return 'superseded' as const;
                    if (navigationId !== this.navigationId())
                        return 'superseded' as const;
                    this.staged = {
                        navigationId,
                        denialVersion: this.deniedVersion,
                        buildingId,
                        result,
                    };
                    if (
                        buildingId === this.activeBuildingId() ||
                        !this.router.currentNavigation?.()
                    ) {
                        if (buildingId !== this.activeBuildingId())
                            this.resetOwner(buildingId);
                        this.apply(result);
                    }
                    return result.kind;
                })
            );
        });
    }

    public ensureBuildingPermission$(
        buildingId: string
    ): Observable<QuerySettledStatus> {
        return this.verifyForNavigation$(buildingId).pipe(
            map((result) => (result === 'granted' ? 'success' : 'error'))
        );
    }
    public discardUnsavedChangesAfterAccessLoss(): boolean {
        if (!this.accessLost()) return false;
        if (this.lossNotice) this.lossNotice.unsavedChanges = true;
        return true;
    }
    public reload(): void {
        this.fetch$().subscribe();
    }
    public fetchAsync(): Promise<QuerySettledStatus> {
        return firstValueFrom(this.fetch$());
    }
    public fetch$(): Observable<QuerySettledStatus> {
        return this.resolveDeniedRequest$().pipe(
            map((result) => (result === 'ordinary' ? 'success' : 'error'))
        );
    }

    private resolveDeniedRequest$(): Observable<BuildingDenialDisposition> {
        if (!this.isAlive() || this.accessLost())
            return of(this.accessLost() ? 'access-lost' : 'superseded');
        this.deniedVersion++;
        if (this.denialCheck) return this.denialCheck;
        const id = this.activeBuildingId();
        const generation = this.generation;
        this.query.setLoading();
        this.decision.set('checking');
        // An older navigation probe cannot establish freshness after a denial.
        const previous: Observable<unknown> = this.probes.get(id) ?? of(null);
        const check = (): Observable<ProbeResult> =>
            defer(() => {
                const version = this.deniedVersion;
                return this.probe$(id).pipe(
                    switchMap((result) =>
                        result.kind === 'granted' &&
                        version !== this.deniedVersion
                            ? check()
                            : of(result)
                    )
                );
            });
        const result$: Observable<BuildingDenialDisposition> = previous.pipe(
            switchMap(() => check()),
            map((result) => {
                if (
                    !this.isAlive() ||
                    generation !== this.generation ||
                    id !== this.activeBuildingId()
                )
                    return 'superseded' as const;
                this.apply(result);
                if (result.kind === 'granted') return 'ordinary' as const;
                if (result.kind === 'lost') return 'access-lost' as const;
                return result.kind === 'superseded'
                    ? ('superseded' as const)
                    : ('verification-unavailable' as const);
            }),
            finalize(() => {
                if (this.denialCheck === result$) this.denialCheck = null;
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this.denialCheck = result$;
        return result$;
    }

    private probe$(buildingId: string): Observable<ProbeResult> {
        const existing = this.probes.get(buildingId);
        if (existing) return existing;
        const identity = this.identity();
        const generation = this.generation;
        const context = createHttpContextFromOptions(
            { suppressAutoErrorToast: true },
            [[BUILDING_PERMISSION_PROBE, true]]
        );
        const result$ = defer(() =>
            this.usersApi.getCurrentUserBuildingPermissions(buildingId, {
                context,
            })
        ).pipe(
            timeout(FETCH_TIMEOUT),
            map((permission): ProbeResult => {
                const me = this.user.me();
                const org = this.user.org();
                if (
                    !this.isAlive() ||
                    identity !== this.identity() ||
                    generation !== this.generation
                )
                    return { kind: 'superseded' };
                if (
                    permission.buildingId !== buildingId ||
                    (me && permission.userId !== me.id) ||
                    (org && permission.organisationId !== org.id) ||
                    !Array.isArray(permission.permissions)
                ) {
                    return {
                        kind: 'unavailable',
                        problem: {
                            status: 0,
                            title: 'Unable to verify building access',
                        },
                    };
                }
                if (
                    permission.assignedRole == null &&
                    permission.permissions.length === 0
                )
                    return { kind: 'lost' };
                return { kind: 'granted', permission, checkedAt: Date.now() };
            }),
            catchError(
                (error: ResolvedProblemDetails): Observable<ProbeResult> =>
                    of(
                        !this.isAlive() ||
                            identity !== this.identity() ||
                            generation !== this.generation
                            ? { kind: 'superseded' }
                            : error.status === 404
                              ? { kind: 'lost' }
                              : {
                                    kind:
                                        error.status === 401
                                            ? 'unauthenticated'
                                            : 'unavailable',
                                    problem: {
                                        ...error,
                                        status: error.status ?? 0,
                                        title:
                                            error.title ??
                                            'Unable to verify building access',
                                    },
                                }
                    )
            ),
            takeUntil(this.destroyed$),
            tap(() => {
                if (this.probes.get(buildingId) === result$)
                    this.probes.delete(buildingId);
            }),
            finalize(() => {
                if (this.probes.get(buildingId) === result$)
                    this.probes.delete(buildingId);
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this.probes.set(buildingId, result$);
        return result$;
    }

    private apply(result: ProbeResult): void {
        if (result.kind === 'superseded') return;
        if (this.accessLost()) return;
        this.decision.set(result.kind);
        if (result.kind === 'granted') {
            this.query.setData(result.permission);
            this.checkedAt = result.checkedAt;
            if (!this.unbind) this.bindOwner();
        } else if (result.kind === 'lost') {
            const name = this.buildingPermission()?.buildingName ?? null;
            this.query.reset();
            this.query.setError({
                status: 404,
                title: 'Building access unavailable',
            });
            this.lossNotice = {
                buildingId: this.activeBuildingId(),
                buildingName: name,
                unsavedChanges: false,
            };
            this.loss.next(this.lossNotice);
            this.endOwner();
        } else {
            this.query.setError(result.problem);
        }
    }

    private bindOwner(): void {
        const generation = this.generation;
        const buildingId = this.activeBuildingId();
        this.unbind = this.recovery.bind({
            buildingId,
            ended$: this.accessEnded$,
            isCurrent: () =>
                this.isAlive() &&
                this.generation === generation &&
                this.activeBuildingId() === buildingId,
            resolveDeniedRequest$: () =>
                this.generation === generation
                    ? this.resolveDeniedRequest$()
                    : of('superseded'),
        });
    }
    private endOwner(): void {
        this.unbind?.();
        this.unbind = null;
        this.ended.next();
        this.ended.complete();
    }
    private resetOwner(buildingId: string): void {
        this.staged = null;
        this.endOwner();
        this.ended = new ReplaySubject<void>(1);
        this.generation++;
        this.ownerIdentity = this.identity();
        this.ownerAccount = this.auth.activeAccount()?.sub;
        this.activeBuildingId.set(buildingId);
        this.query.reset();
        this.checkedAt = 0;
        this.decision.set('checking');
        this.denialCheck = null;
        this.probes.clear();
    }
    private principalReady(): boolean {
        return (
            !this.loggingOut &&
            !!this.auth.activeAccount()?.sub &&
            !!this.user.me()?.id &&
            !!this.user.org()?.id
        );
    }
    private isAlive(): boolean {
        return (
            !this.disposed &&
            !this.loggingOut &&
            this.ownerIdentity === this.identity()
        );
    }
    private navigationId(): number {
        return this.router.currentNavigation?.()?.id ?? 0;
    }
    private readCurrentBuildingId(): string {
        return (
            findRouteParamInSnapshot(
                this.router.routerState.snapshot.root,
                'buildingId'
            ) ?? ''
        );
    }
}
